/**
 * Session events that named no stamp, and what the policy would have decided.
 *
 * The Overview counts these. A number meaning "these sessions are settling
 * somewhere nobody chose" had nowhere to go: no list, no stations, no way to
 * turn it into an action. This is that list.
 *
 * It decides nothing. DEC-02 stands: orphans are counted, not blocked. The
 * page exists so the count can be read, during a migration of 1881 stations
 * where the number is expected to move and somebody has to be able to say
 * whether it is moving for the right reason.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  Input,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  DataTableShell,
  EmptyState,
  FilterBar,
  IDChip,
  LoadingState,
  MonoText,
  StatusBadge,
  formatTimestamp,
} from '../platform'
import { api } from '../api'
import type { OrphanSessionEvent } from '../api'
import { orphanRoute, orphanForward, orphanEstate, orphanDecision } from './orphanReading'

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  table: { width: '100%' },
  timeColumn: { width: '210px' },
  stationColumn: { width: '190px' },
  nowrap: { whiteSpace: 'nowrap' },
  why: { color: tokens.colorNeutralForeground3 },
  search: { width: '320px' },
})

export default function OrphanSessions() {
  const styles = useStyles()
  const [rows, setRows] = useState<OrphanSessionEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [station, setStation] = useState('')

  const load = useCallback(async (forStation?: string) => {
    setRows(null)
    try {
      const query = forStation ? `?station_id=${encodeURIComponent(forStation)}` : ''
      const page = await api.get<{ items: OrphanSessionEvent[] }>(
        `/orphan-session-events${query}`,
      )
      setRows(page.items ?? [])
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The service is not answering')
      setRows([])
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const shown = rows ?? []

  return (
    <div className={styles.page}>
      <FilterBar
        search={
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void load(station || undefined)
            }}
          >
            <Input
              className={styles.search}
              value={station}
              onChange={(_, data) => setStation(data.value)}
              placeholder="Charge point id"
              aria-label="Narrow to one charge point"
            />
          </form>
        }
      />
      <DataTableShell>
        {rows === null ? (
          <LoadingState label="Reading the orphan events" />
        ) : error ? (
          <EmptyState title="Nothing to show" description={error} />
        ) : shown.length === 0 ? (
          <EmptyState
            title="No orphan session events"
            description={
              station
                ? `Nothing at ${station}. Every session event from it named a stamp.`
                : 'Every session event this service has seen named a stamp, so every one of them settles against the authority that authorized it.'
            }
          />
        ) : (
          <Table size="small" className={styles.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={styles.timeColumn}>Time, Berlin</TableHeaderCell>
                <TableHeaderCell className={styles.stationColumn}>Charge point</TableHeaderCell>
                <TableHeaderCell>In estate</TableHeaderCell>
                <TableHeaderCell>Event</TableHeaderCell>
                <TableHeaderCell>Card type</TableHeaderCell>
                <TableHeaderCell>Identifier</TableHeaderCell>
                <TableHeaderCell>Would have been</TableHeaderCell>
                <TableHeaderCell>Why</TableHeaderCell>
                <TableHeaderCell>Forward</TableHeaderCell>
                <TableHeaderCell>Decision</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((event) => {
                const estate = orphanEstate(event.in_estate)
                const forward = orphanForward(event)
                return (
                  <TableRow key={event.id}>
                    <TableCell className={styles.nowrap}>
                      <MonoText>{formatTimestamp(event.observed_at)}</MonoText>
                    </TableCell>
                    <TableCell>
                      <MonoText>{event.station_id}</MonoText>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={estate.status} label={estate.label} />
                    </TableCell>
                    <TableCell className={styles.nowrap}>{event.operation}</TableCell>
                    <TableCell>{event.scheme ?? ''}</TableCell>
                    <TableCell>
                      {/* The blind index, which is what the service stores.
                          The identifier itself is personal data and never
                          leaves the database in plaintext. */}
                      {event.identifier_blind_index ? (
                        <IDChip value={event.identifier_blind_index} />
                      ) : null}
                    </TableCell>
                    <TableCell>{orphanRoute(event)}</TableCell>
                    <TableCell className={styles.why}>
                      <span title={event.route_steps?.join(' → ')}>
                        {event.route_steps?.join(' → ') ?? ''}
                      </span>
                    </TableCell>
                    <TableCell className={styles.nowrap}>{forward}</TableCell>
                    {/* Whether a decision this service already recorded
                        explains this row. A blank here would say no
                        authorization existed, which for almost every row is
                        the opposite of what happened. */}
                    <TableCell className={styles.why}>
                      <span title={event.decision?.detail ?? undefined}>
                        {orphanDecision(event.decision)}
                      </span>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </DataTableShell>
    </div>
  )
}
